import { useEffect, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import {
  DropdownOptionsType,
  DropdownType,
  GenericPayloadDataType,
  getGenericEntityListingAPI,
  hideLoader,
  showLoader,
  WorkflowInstanceTaskType,
} from '@app-store'
import {
  editEntityDataEmbeddedAPI,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  PAP_NAMESPACES,
  NPI_STATUS_CUSTOM_LAYOUT,
  NPI_STATUS_TABLE_COLUMNS,
  NPI_STATUS_CONSTANTS,
} from '../../utils/pap.constants'
import { ControlledDropDown, CustomButton, FormInput, Accordion } from '@ui-kit'
import { NAMESPACES, TASK_STATUS } from '../../utils/constants'
import TaskNotesSuggestionBox from './TaskNotesSuggestion'
import { isEmpty } from 'lodash'
import { formatNumberTwoDigits } from '../../utils/helperFunctions'
import { currentUserId } from '../../utils/permission'
import moment from 'moment'

const NPIStatusCustom: React.FC<{
  recordId: string
  task: WorkflowInstanceTaskType
  isDisabled?: boolean
}> = ({ task, recordId, isDisabled }) => {
  const [isViewMode, setIsViewMode] = useState<boolean>(false)
  const [dropdownValues, setDropdownValues] = useState([])
  const [tableData, setTableData] = useState<GenericPayloadDataType[]>([])
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false)
  const [taskNoteBoxId, setTaskNoteBoxId] = useState<string>('')
  const { register, setValue, handleSubmit, control, getValues } = useForm()
  const dispatch = useAppDispatch()

  const workflowDefinition = useAppSelector(
    (state: RootState) => state.workflowData?.workflowDefinition
  )

  const onSubmit = async (data: FieldValues) => {
    try {
      dispatch(showLoader())
      const newPayload = {
        entity_name: workflowDefinition?.global_variables?.entity_name,
        select: ['case_pap_details'],
        criteria: [],

        filters: [],
      }

      const caseIdObject2 = await getGenericEntityListingAPI(
        newPayload,
        recordId ?? ''
      )
      const submitData = {
        provider_npi_status: data.provider_npi_status,
        _state: NAMESPACES.CHANGED,
      }

      const res = await editEntityDataEmbeddedAPI(
        NPI_STATUS_CONSTANTS.CASE_PAP_DETAILS_ENTITY_NAME,
        caseIdObject2?.case_pap_details?.id,
        submitData,
        [NPI_STATUS_CONSTANTS.FIELD_PROVIDER_NPI_STATUS],
        false
      )

      if (res.data) {
        await editEntityDataEmbeddedAPI(
          workflowDefinition?.global_variables?.entities?.task?.entity_name ??
            NAMESPACES.TASK,
          task?.id ?? '',
          {
            c_case_notes_task: [
              {
                name: getValues(`taskNotes${task?.task_id}`),
                source: NAMESPACES.TASK,
                headers: `T${formatNumberTwoDigits(
                  task?.task_id ? +task?.task_id : 0
                )}- ${task?.name}`,
                ...(taskNoteBoxId ? { id: taskNoteBoxId } : {}),
                _state: NAMESPACES.CHANGED,
                case: {
                  id: task?.case_id,
                  _state: NAMESPACES.CHANGED,
                },
                ...(!taskNoteBoxId ? { users_id: currentUserId() ?? '' } : {}),
              },
            ],
            record_status: TASK_STATUS.ACTIVE,
          },
          ['c_case_notes_task']
        )

        setIsViewMode(true)
        fetchNPIStatusDetails()
        fetchGenericPayloadData()
        fetchTaskNotesData()
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchProviderNPI = async () => {
    try {
      const caseResponse = await getEntityDataByIdEmbedded(
        NPI_STATUS_CONSTANTS.CASE_ENTITY_NAME,
        NPI_STATUS_CONSTANTS.CASE_ENTITY_ID,
        [NPI_STATUS_CONSTANTS.FIELD_PROVIDER_ID],
        recordId
      )

      if (caseResponse.data?.provider_id) {
        const providerResponse = await getEntityDataByIdEmbedded(
          NPI_STATUS_CONSTANTS.PROVIDERS_ENTITY_NAME,
          NPI_STATUS_CONSTANTS.PROVIDERS_ENTITY_ID,
          [NPI_STATUS_CONSTANTS.FIELD_NPI],
          caseResponse.data.provider_id
        )

        if (providerResponse?.data?.npi) {
          setValue('provider_npi', providerResponse.data.npi)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }
  const fetchListForDropdown = async () => {
    try {
      const payload = {
        entity_name: NPI_STATUS_CONSTANTS.ENTITY_FIELDS_ENTITY_NAME,
        select: NPI_STATUS_CONSTANTS.ENTITY_FIELDS_SELECT,
        criteria: [],
        filters: [
          {
            criteria: [
              {
                values: [NPI_STATUS_CONSTANTS.ENTITY_FIELDS_ENTITY_ID],
                operation: NPI_STATUS_CONSTANTS.OPERATION_EQUALS,
                field_name: NPI_STATUS_CONSTANTS.FIELD_D_ENTITY_ID,
              },
              {
                values: [NPI_STATUS_CONSTANTS.FIELD_PROVIDER_NPI_STATUS],
                operation: NPI_STATUS_CONSTANTS.OPERATION_EQUALS,
                field_name: NPI_STATUS_CONSTANTS.FIELD_NAME,
              },
            ],
          },
        ],
      }
      const caseIdObject1 = await getGenericEntityListingAPI(payload)
      const transformed = caseIdObject1?.[0]?.control_values?.map(
        (item: FieldValues) => ({
          value: item,
          label: item,
        })
      )
      setDropdownValues(transformed)
    } catch (err) {
      console.error(err)
    }
  }
  const fetchNPIStatusDetails = async () => {
    try {
      if (!recordId) return
      const payload = {
        select: [NPI_STATUS_CONSTANTS.CASE_PAP_DETAILS_ENTITY_NAME],
        entity_name: NPI_STATUS_CONSTANTS.CASE_ENTITY_NAME,
        related_entities: {
          [NPI_STATUS_CONSTANTS.CASE_PAP_DETAILS_ENTITY_NAME]: {
            select: [NPI_STATUS_CONSTANTS.FIELD_PROVIDER_NPI_STATUS],
            criteria: [],
          },
        },
      }
      const res = await getGenericEntityListingAPI(payload, recordId)

      setValue(
        NPI_STATUS_CONSTANTS.FIELD_PROVIDER_NPI_STATUS,
        res?.case_pap_details?.provider_npi_status
      )

      if (res?.case_pap_details?.provider_npi_status) {
        setIsViewMode(true)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleEditClick = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    setIsViewMode(false)
  }

  const fetchGenericPayloadData = async () => {
    try {
      setIsLoadingTable(true)
      const payload = {
        select: NPI_STATUS_CONSTANTS.GENERIC_PAYLOAD_SELECT,
        filters: [
          {
            logical_operator: NPI_STATUS_CONSTANTS.LOGICAL_OPERATOR_AND,
            criteria: [
              {
                operation: NPI_STATUS_CONSTANTS.OPERATION_EQUALS,
                field_name: NPI_STATUS_CONSTANTS.FIELD_GROUP_ID,
                values: [NPI_STATUS_CONSTANTS.GENERIC_PAYLOAD_GROUP_ID],
              },
              {
                field_name: NPI_STATUS_CONSTANTS.FIELD_CASE_ID,
                values: [recordId],
                operation: NPI_STATUS_CONSTANTS.OPERATION_EQUALS,
              },
            ],
          },
        ],
        entity_name: NPI_STATUS_CONSTANTS.GENERIC_PAYLOAD_ENTITY_NAME,
      }
      const response = await getGenericEntityListingAPI(payload)
      if (response && Array.isArray(response)) {
        const formattedData = response.map((item: GenericPayloadDataType) => ({
          ...item,
          created_at: item.created_at
            ? moment(item.created_at).format(NPI_STATUS_CONSTANTS.DATE_FORMAT)
            : '-',
        }))
        setTableData(formattedData)
      } else {
        setTableData([])
      }
    } catch (err) {
      console.error(err)
      setTableData([])
    } finally {
      setIsLoadingTable(false)
    }
  }

  const fetchTaskNotesData = async () => {
    const payload = {
      entity_name:
        workflowDefinition?.global_variables?.entities?.task?.entity_name ??
        NAMESPACES.TASK,
      select: ['c_case_notes_task', NAMESPACES.NAME_KEY],
      filters: [
        {
          criteria: [],
        },
      ],
      related_entities: {
        c_case_notes_task: {
          select: [NAMESPACES.NAME_KEY],
          criteria: [
            {
              criteria: [],
            },
          ],
        },
      },
    }
    const responseData = await getGenericEntityListingAPI(payload, task?.id)
    if (responseData?.c_case_notes_task?.[0]?.id) {
      setTaskNoteBoxId(responseData?.c_case_notes_task?.[0]?.id)
      setValue(
        `taskNotes${task?.task_id}`,
        responseData?.c_case_notes_task?.[0]?.name
      )
    } else if (
      !isEmpty(task?.identifier?.order_notes) &&
      task?.identifier?.taskNoteValues?.isTaskNoteSuggestionBox
    ) {
      editEntityDataEmbeddedAPI(
        workflowDefinition?.global_variables?.entities?.task?.entity_name ??
          NAMESPACES.TASK,
        task?.id ?? '',
        {
          c_case_notes_task: [
            {
              name: task?.identifier?.order_notes,
              source: NAMESPACES.TASK,
              headers: `T${formatNumberTwoDigits(
                task?.task_id ? +task?.task_id : 0
              )}- ${task?.name}`,
              ...(taskNoteBoxId ? { id: taskNoteBoxId } : {}),
              _state: NAMESPACES.CHANGED,
              case: {
                id: task?.case_id,
                _state: NAMESPACES.CHANGED,
              },
              ...(!taskNoteBoxId ? { users_id: currentUserId() ?? '' } : {}),
            },
          ],
          record_status: TASK_STATUS.ACTIVE,
        },
        ['c_case_notes_task']
      )
      setValue(`taskNotes${task?.task_id}`, task?.identifier?.order_notes)
    }
  }

  useEffect(() => {
    if (task) {
      fetchProviderNPI()
      fetchListForDropdown()
      fetchNPIStatusDetails()
      fetchGenericPayloadData()
      fetchTaskNotesData()
    }
  }, [task])

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit(onSubmit)} id="npi-status-form">
        {/* Edit/Update Button */}
        <div className="flex justify-end pb-2">
          {!isDisabled && (
            <CustomButton
              type={isViewMode ? 'button' : 'submit'}
              form={isViewMode ? undefined : 'npi-status-form'}
              onClick={isViewMode ? handleEditClick : undefined}
              isFilled
              dataQaId={isViewMode ? 'edit-button' : 'update-button'}
              data-mode={isViewMode ? 'edit' : 'update'}
            >
              {isViewMode ? NAMESPACES.EDIT : NAMESPACES.UPDATE_BUTTON}
            </CustomButton>
          )}
        </div>

        <Accordion
          heading={NPI_STATUS_CONSTANTS.ACCORDION_HEADING}
          isDefaultActive={true}
        >
          <div className="p-4">
            <div className="flex items-end gap-4">
              {/* Provider NPI */}
              <div className="flex-1">
                <FormInput
                  register={register}
                  name="provider_npi"
                  label={NPI_STATUS_CONSTANTS.PROVIDER_NPI_LABEL}
                  disabled={true}
                />
              </div>

              {/* Dropdown */}
              <div className="flex-1">
                <ControlledDropDown
                  disabled={isDisabled || isViewMode}
                  control={control}
                  label={NPI_STATUS_CUSTOM_LAYOUT.PROVIDER_NPI_STATUS_LABEL}
                  name={NPI_STATUS_CUSTOM_LAYOUT.PROVIDER_NPI_STATUS_KEY}
                  onChange={(e: DropdownOptionsType) => {
                    setValue(
                      NPI_STATUS_CUSTOM_LAYOUT.PROVIDER_NPI_STATUS_KEY,
                      e.value
                    )
                  }}
                  options={(dropdownValues as DropdownType[]) ?? []}
                  isSearchable
                />
              </div>
            </div>

            <div className="mt-6">
              {isLoadingTable ? (
                <div className="text-center py-4">
                  {PAP_NAMESPACES.LOADING_DATA}
                </div>
              ) : tableData.length === 0 ? (
                <div className="text-center py-4">
                  {PAP_NAMESPACES.NO_GENERIC_PAYLOAD_RECORDS_FOUND}
                </div>
              ) : (
                <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                  <table className="min-w-full border border-white text-sm">
                    <thead className="bg-secondary-100 sticky top-0">
                      <tr>
                        {NPI_STATUS_TABLE_COLUMNS.filter(
                          (col) => !col.isHidden
                        ).map((col) => (
                          <th
                            key={col.accessor}
                            className="bg-secondary-50 py-2 px-2 text-left font-medium"
                            scope="col"
                          >
                            {col.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className="bg-white border-b border-gray-200"
                        >
                          {NPI_STATUS_TABLE_COLUMNS.filter(
                            (col) => !col.isHidden
                          ).map((col) => (
                            <td
                              key={`${rowIndex}-${col.accessor}`}
                              className="py-2 px-2"
                            >
                              {row[col.accessor] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div className="mt-4">
              <TaskNotesSuggestionBox
                task={task}
                mode={!isDisabled && !isViewMode ? 'edit' : 'view'}
                setValue={setValue}
                register={register}
                isDisabled={isDisabled || isViewMode}
                isShowSuggestion={
                  task?.identifier?.taskNoteValues?.isTaskNoteAddButton
                }
              />
            </div>
          </div>
        </Accordion>
      </form>
    </div>
  )
}

export default NPIStatusCustom
